import type { AtendimentoModo } from '../types';

// A sacola do atendimento é a mesma do app (Dexie, por origem, vale para todas
// as abas). Sem esta marca, qualquer tela do app que mostre a sacola — a loja
// `/:slug` ("Ver sacola"), a revisão e o checkout `/:slug/order` — trata os
// itens do atendimento como um pedido de delivery. A marca diz que a sacola
// desta loja foi montada no atendimento; o checkout do app devolve para lá.
export const CARRINHO_ATENDIMENTO_KEY = 'atendimento-carrinho';

// Depois disso a sacola deixa de ser "do atendimento" (cliente foi embora).
const VALIDADE_MS = 3 * 60 * 60 * 1000;

type Marca = { slug: string; modo: AtendimentoModo; em: number };

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function marcarCarrinhoAtendimento(
  slug: string,
  modo: AtendimentoModo,
  storage: StorageLike | null = defaultStorage(),
  agora = Date.now()
) {
  try {
    storage?.setItem(CARRINHO_ATENDIMENTO_KEY, JSON.stringify({ slug, modo, em: agora }));
  } catch {
    // sem persistência
  }
}

export function limparCarrinhoAtendimento(
  slug?: string,
  storage: StorageLike | null = defaultStorage()
) {
  try {
    if (slug && lerCarrinhoAtendimento(storage)?.slug !== slug) return;
    storage?.removeItem(CARRINHO_ATENDIMENTO_KEY);
  } catch {
    // ignora
  }
}

export function lerCarrinhoAtendimento(
  storage: StorageLike | null = defaultStorage(),
  agora = Date.now()
): Marca | null {
  try {
    const raw = storage?.getItem(CARRINHO_ATENDIMENTO_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as Partial<Marca>;
    if (typeof data.slug !== 'string' || !data.slug) return null;
    if (data.modo !== 'cliente' && data.modo !== 'assistido') return null;
    if (typeof data.em !== 'number' || agora - data.em > VALIDADE_MS) return null;
    return { slug: data.slug, modo: data.modo, em: data.em };
  } catch {
    return null;
  }
}

const CHECKOUT_DO_APP = /^\/([^/?#]+)\/order\/?$/;

// Se `pathname` é o checkout do app de uma loja cuja sacola é do atendimento,
// devolve para onde ir: o pagamento do atendimento (cliente) ou o PDV
// (operador). Senão, null.
export function destinoDoCheckout(pathname: string, marca: Marca | null): string | null {
  const match = pathname.match(CHECKOUT_DO_APP);
  if (!match || !marca) return null;
  const slug = decodeURIComponent(match[1]);
  if (slug !== marca.slug) return null;
  return marca.modo === 'assistido'
    ? `/${slug}/atendimento/cardapio`
    : `/${slug}/atendimento?pagar=1`;
}

// Estado do checkout do app (localStorage), que ele restaura ao voltar do
// login. Do atendimento nunca se vai para lá: limpa antes do login.
export function limparCheckoutDoApp(storage: StorageLike | null = defaultStorage()) {
  try {
    storage?.removeItem('checkout-state');
  } catch {
    // ignora
  }
}
